import { forwardRef, type HTMLAttributes } from 'react';
import { cn } from '../../utils/cn';
import { useDsT } from '../../services/i18n';

export type KbdSize = 'sm' | 'md';

export interface KbdProps extends Omit<HTMLAttributes<HTMLElement>, 'children'> {
  /** Shortcut spec, e.g. "mod+K", "shift+enter", "ctrl+alt+delete". */
  keys?: string;
  /** Raw content when `keys` is not used. */
  children?: string;
  size?: KbdSize;
  /** Force a platform (defaults to detection via navigator). */
  platform?: 'mac' | 'other';
}

/** True on Apple platforms; safe fallback to false outside the browser. */
export function isMacPlatform(): boolean {
  if (typeof navigator === 'undefined') return false;
  const platform = navigator.platform ?? '';
  const ua = navigator.userAgent ?? '';
  return /Mac|iPhone|iPad|iPod/i.test(platform) || /Macintosh|Mac OS X/i.test(ua);
}

interface KeyGlyph {
  glyph: string;
  name: string;
}

const macGlyphs: Record<string, KeyGlyph> = {
  mod: { glyph: '⌘', name: 'Command' },
  cmd: { glyph: '⌘', name: 'Command' },
  meta: { glyph: '⌘', name: 'Command' },
  ctrl: { glyph: '⌃', name: 'Control' },
  control: { glyph: '⌃', name: 'Control' },
  alt: { glyph: '⌥', name: 'Option' },
  option: { glyph: '⌥', name: 'Option' },
  shift: { glyph: '⇧', name: 'Shift' },
};

const otherGlyphs: Record<string, KeyGlyph> = {
  mod: { glyph: 'Ctrl', name: 'Control' },
  cmd: { glyph: 'Win', name: 'Windows' },
  meta: { glyph: 'Win', name: 'Windows' },
  ctrl: { glyph: 'Ctrl', name: 'Control' },
  control: { glyph: 'Ctrl', name: 'Control' },
  alt: { glyph: 'Alt', name: 'Alt' },
  option: { glyph: 'Alt', name: 'Alt' },
  shift: { glyph: 'Shift', name: 'Shift' },
};

const commonGlyphs: Record<string, KeyGlyph> = {
  enter: { glyph: '↵', name: 'Enter' },
  return: { glyph: '↵', name: 'Enter' },
  esc: { glyph: 'Esc', name: 'Escape' },
  escape: { glyph: 'Esc', name: 'Escape' },
  tab: { glyph: '⇥', name: 'Tab' },
  space: { glyph: 'Space', name: 'Space' },
  backspace: { glyph: '⌫', name: 'Backspace' },
  delete: { glyph: '⌦', name: 'Delete' },
  del: { glyph: '⌦', name: 'Delete' },
  up: { glyph: '↑', name: 'Arrow up' },
  arrowup: { glyph: '↑', name: 'Arrow up' },
  down: { glyph: '↓', name: 'Arrow down' },
  arrowdown: { glyph: '↓', name: 'Arrow down' },
  left: { glyph: '←', name: 'Arrow left' },
  arrowleft: { glyph: '←', name: 'Arrow left' },
  right: { glyph: '→', name: 'Arrow right' },
  arrowright: { glyph: '→', name: 'Arrow right' },
  home: { glyph: 'Home', name: 'Home' },
  end: { glyph: 'End', name: 'End' },
  pageup: { glyph: 'PgUp', name: 'Page up' },
  pagedown: { glyph: 'PgDn', name: 'Page down' },
};

/** Parses a shortcut spec into platform-aware glyphs with screen-reader names. */
export function parseKeys(spec: string, mac: boolean): KeyGlyph[] {
  const mods = mac ? macGlyphs : otherGlyphs;
  return spec
    .split('+')
    .map((k) => k.trim())
    .filter(Boolean)
    .map((k) => {
      const lower = k.toLowerCase();
      const found = mods[lower] ?? commonGlyphs[lower];
      if (found) return found;
      const glyph = k.length === 1 ? k.toUpperCase() : k;
      return { glyph, name: glyph };
    });
}

/* `min-w` matches the height so a single key renders square. */
const sizeMap: Record<KbdSize, string> = {
  sm: 'h-4 min-w-4 px-1 type-caption',
  md: 'h-5 min-w-5 px-1.5 type-caption',
};

/*
 * Design Direction v2: a key is a small raised surface — `bg-surface-2` with the hairline folded into
 * `shadow-1`, mono type, `rounded-sm`. No border of its own.
 */
const kbdClass =
  'inline-flex items-center justify-center rounded-sm bg-surface-2 font-mono font-strong leading-none text-fg-muted shadow-1';

export const Kbd = forwardRef<HTMLElement, KbdProps>(function Kbd({ keys, children, size = 'md', platform, className, ...rest }, ref) {
  const mac = platform ? platform === 'mac' : isMacPlatform();
  const t = useDsT();
  if (!keys) {
    return (
      <kbd ref={ref} className={cn('zo-kbd', kbdClass, sizeMap[size], className)} {...rest}>
        {children}
      </kbd>
    );
  }
  const parts = parseKeys(keys, mac);
  const spoken = (name: string) => t(`ds.kbd.${name.toLowerCase().replace(/\s+(\w)/g, (_, c: string) => c.toUpperCase())}`, name);
  return (
    <kbd ref={ref} className={cn('zo-kbd inline-flex items-center gap-0.5 font-mono', className)} data-platform={mac ? 'mac' : 'other'} {...rest}>
      {parts.map((p, i) => (
        <kbd key={`${p.name}-${i}`} className={cn(kbdClass, sizeMap[size])}>
          {p.glyph === p.name ? (
            p.glyph
          ) : (
            <>
              <span aria-hidden="true">{p.glyph}</span>
              <span className="sr-only">{spoken(p.name)}</span>
            </>
          )}
        </kbd>
      ))}
    </kbd>
  );
});
